// RigSentinel Owner Dashboard V1 — recharts wrappers. Presentational only;
// every number here already came pre-computed from the backend (no
// client-side entitlement math, no engineering data of any kind).
import React from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";

const AMBER = "#FFB300";
const BLUE = "#60A5FA";
const GREEN = "#4CAF50";
const RED = "#F44336";
const PURPLE = "#A78BFA";
const GREY = "#8B95A0";
const PALETTE = [AMBER, BLUE, GREEN, PURPLE, RED, GREY];

const AXIS_STYLE = { fontSize: 10, fill: "#8B95A0" };
const TOOLTIP_STYLE = {
  background: "#16181D", border: "1px solid #3D4354", borderRadius: 6,
  fontSize: 12, color: "#F8F9FA",
};

export function ChartCard({ title, testId, empty, children }) {
  return (
    <section className="card-rs p-4" data-testid={testId}>
      <h3 className="text-[12px] label-up text-[var(--rs-text-3)] mb-3">{title}</h3>
      {empty ? (
        <p className="text-[12px] text-[var(--rs-text-3)] py-8 text-center">{empty}</p>
      ) : children}
    </section>
  );
}

export function TrendLineChart({ series, xKey, yKey, color = AMBER }) {
  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#2A2E39" />
        <XAxis dataKey={xKey} tick={AXIS_STYLE} minTickGap={20} />
        <YAxis tick={AXIS_STYLE} allowDecimals={false} />
        <Tooltip contentStyle={TOOLTIP_STYLE} />
        <Line type="monotone" dataKey={yKey} stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function MixBarChart({ data, dataKey = "value", nameKey = "label" }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#2A2E39" horizontal={false} />
        <XAxis type="number" tick={AXIS_STYLE} allowDecimals={false} />
        <YAxis type="category" dataKey={nameKey} tick={AXIS_STYLE} width={150} />
        <Tooltip contentStyle={TOOLTIP_STYLE} />
        <Bar dataKey={dataKey} fill={AMBER} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function MixPieChart({ data, dataKey = "value", nameKey = "label" }) {
  const total = data.reduce((s, d) => s + (d[dataKey] || 0), 0);
  if (total === 0) return <p className="text-[12px] text-[var(--rs-text-3)] py-8 text-center">No accounts yet.</p>;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={data} dataKey={dataKey} nameKey={nameKey} cx="50%" cy="50%" outerRadius={80} label={{ fontSize: 10, fill: "#8B95A0" }}>
          {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Pie>
        <Tooltip contentStyle={TOOLTIP_STYLE} />
        <Legend wrapperStyle={{ fontSize: 11, color: "#8B95A0" }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function FunnelBars({ stages }) {
  return (
    <div className="flex flex-col gap-2">
      {stages.map((s, i) => {
        const max = Math.max(1, ...stages.map((x) => x.value));
        return (
          <div key={s.label} data-testid={`funnel-stage-${i}`}>
            <div className="flex justify-between text-[11px] mb-0.5">
              <span className="text-[var(--rs-text-2)]">{s.label}</span>
              <span className="text-[var(--rs-text-3)] font-mono-rs">{s.value}</span>
            </div>
            <div className="h-2 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
              <div className="h-2 rounded-full" style={{ width: `${(s.value / max) * 100}%`, background: PALETTE[i % PALETTE.length] }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
